import {binarySearch} from "../utils/search";
import {NO_VOTE} from "./votes";
import {defaultSettings} from "./settings";

export const selectUsername = ({settings}) => settings.username;

export const selectLoggedIn = ({settings}) => Boolean(settings.username);

export const selectProfile = ({settings}) => settings || defaultSettings;

export const selectIsBlocked = ({blocked}) => {
    return (username) => typeof username === 'string' && binarySearch(blocked, username) !== -1;
};

export const selectFeedEntry = (key) => ({postLists}) => {
    return postLists.get(key);
};

export const selectFeedIsLoaded = (key) => ({postLists}) => {
    const feed = postLists.get(key);
    return Boolean(feed) && ('ids' in feed);
};

export const selectFeedLastPost = (key) => ({postLists, postMap}) => {
    const {ids} = postLists.get(key);
    const lastPostId = ids[ids.length - 1];
    return postMap.get(lastPostId);
};

export const selectPosts = (ids, withHidden=false, withDeleted=false) => ({postMap}) => {
    const posts = ids.map((id) => postMap.get(id));
    return posts.filter((post) => {
        return post && (!post.hidden || withHidden) && (!post.is_deleted || withDeleted);
    });
};

export const selectPostVoteState = (id) => ({postMap}) => {
    const post = postMap.get(id);
    return (!post || !('vote_state' in post)) ? NO_VOTE : post.vote_state;
};

export const selectCommentsPage = (key) => ({ commentLists }) => {
    return commentLists.get(key)?.page || 0;
};

export const selectCommentListLoaded = (key) => ({ commentLists }) =>
    commentLists.has(key);

export const selectCommentList = (key) => ({ commentLists, commentMap }) => {
    const {ids, page, more, loading} = commentLists.get(key) || { ids: [], page: 0, more: false, loading: false };
    const comments = ids.map((id) => id && commentMap.get(id));
    return {comments, page, more: more && !loading, loading: !ids || loading};
};

export const selectCommentThread = (key) => ({ commentLists, commentMap }) => {
    const {ids} = commentLists.get(key) || { ids: [] };
    return ids?.length ? commentMap.get(ids[0]) : null;
};

export const selectComment = (id) => ({commentMap}) => commentMap.get(id);

export const selectCommentsFlat = (key) => (state) => {
    const {commentLists, commentMap} = state;
    const isBlocked = selectIsBlocked(state);
    const items = [];
    function mark(index) {
        if (items.length > 0) {
            items[index].span = items.length - index;
        }
    }
    function collect(id, parent, depth) {
        const comment = commentMap.get(id);
        if (!isBlocked(comment.author)) {
            const key = `c/${id}`;
            const childIds = commentLists.get(key)?.ids || [];
            // XXX Test is flawed, a comment with only deleted children will be shown.
            // if (!comment.is_deleted || childIds.length > 0) {
            const index = items.length;
            items.push({ key, parent: parent - index, depth, comment });
            for (let childIndex = 0; childIndex < childIds.length; childIndex++) {
                collect(childIds[childIndex], index, depth + 1);
            }
            mark(index);
            // }
        }
    }
    const ids = commentLists.get(key)?.ids || [];
    for (let index = 0; index < ids.length; index++) {
        collect(ids[index], -1, 0);
    }
    return items;
};

export const selectThread = (key) => (state) => {
    const comments = selectCommentsFlat(key)(state);
    return { comments };
};

export const selectPost = (id, withComments=false) => (state) => {
    const post = state.postMap.get(id);
    if (!withComments) {
        return { post };
    }
    const commentsKey = `p:${id}`;
    const { more, comments } = selectCommentList(commentsKey)(state);
    return { post, comments, hasMoreComments: more };
};

export const selectContentList = (key) => ({contentLists}) => contentLists.get(key);

export const selectContent = (items) => ({postMap, commentMap}) =>
    items.map(
        (item) => item && (item.type === 'post' ? postMap.get(item.id) : commentMap.get(item.id))
    );

export const selectCommentVoteState = (id) => ({commentMap}) => {
    const comment = commentMap.get(id);
    return (!comment || !('vote_state' in comment)) ? NO_VOTE : comment.vote_state;
};

export const selectCommunities = ({communityMap}, names) =>
    names?.map((name) => name && communityMap.get(name.toLowerCase()));

/** selectStaleCommunities builds a selector that returns the names of
 *  communities that are either not loaded, or have a timestamp older than ref.
 * @param names {string[]}
 * @param ref {number}
 * @returns {function({communityMap: *}): *[]}
 */
export const selectStaleCommunities = (names, ref) => ({communityMap}) => {
    const result = [];
    for (const name of names) {
        const community = communityMap.get(name?.toLowerCase());
        if (!community || (community.timestamp || 0) < ref) {
            result.push(name);
        }
    }
    return result;
}
